import React, { useLayoutEffect, useMemo, useState } from 'react';
import { Html } from 'react-konva-utils';
import h337 from 'heatmap.js';
import { findMinMaxX, findMinMaxY } from '../../track/ground/utils';
import { canonicalToCanvas, GroundContext, localToCanonical, ProcessedPlayer, Viewport } from '../lib';

export type PropsHeatmap = {
  pathData: ProcessedPlayer;
  vp: Viewport;
  ctx: GroundContext;
  offsetX: number;
  offsetY: number;
};

export const HeatMap = ( props: PropsHeatmap) => {
  const {
    pathData, offsetX, offsetY, vp, ctx,
  } = props;

  const groupRef = React.useRef<HTMLDivElement>(null);
  const heatmapInstancerRef = React.useRef<any>(null);

  const gradient = [
    'rgba(0, 0, 255, 0)',
    'rgba(0, 0, 255, 1)',
    'rgba(0, 31, 255, 1)',
    'rgba(0, 63, 255, 1)',
    'rgba(0, 91, 255, 1)',
    'rgba(0, 127, 255, 1)',
    'rgba(0, 159, 255, 1)',
    'rgba(0, 191, 255, 1)',
    'rgba(0, 223, 255, 1)',
    'rgba(0, 255, 255, 1)',
    'rgba(0, 255, 223, 1)',
    'rgba(0, 255, 191, 1)',
    'rgba(0, 255, 159, 1)',
    'rgba(0, 255, 127, 1)',
    'rgba(0, 255, 91, 1)',
    'rgba(0, 255, 63, 1)',
    'rgba(0, 255, 31, 1)',
    'rgba(0, 255, 0, 1)',
    'rgba(31, 223, 0, 1)',
    'rgba(63, 191, 0, 1)',
    'rgba(91, 159, 0, 1)',
    'rgba(127, 127, 0, 1)',
    'rgba(159, 91, 0, 1)',
    'rgba(191, 63, 0, 1)',
    'rgba(223, 31, 0, 1)',
    'rgba(255, 0, 0, 1)',
  ];

  const {minX, maxX} = useMemo(() => findMinMaxX(pathData.samples), [pathData.samples]);
  const {minY, maxY} = useMemo(() => findMinMaxY(pathData.samples), [pathData.samples]);

  const coordMin = canonicalToCanvas(localToCanonical({x: minX, y: minY}, ctx), vp);
  const coordMax = canonicalToCanvas(localToCanonical({x: maxX, y: maxY}, ctx), vp);

  const [mapReady, setMapReady] = useState(false);

  const coordsReady = maxX && maxY && minX && minY;
  const rows: number[] = [];

  useLayoutEffect(() => {
    setTimeout(() => {
      if (groupRef.current && !heatmapInstancerRef.current) {
        heatmapInstancerRef.current = h337.create({
          container: groupRef.current,
          backgroundColor: 'rgba(128, 128, 0, 0.3)',
          radius: 10,
          blur: 1,
          opacity: 0.5,
          gradient: gradient.reduce(( acc, col, idx, arr ) => {
            acc[`${idx / arr.length}`] = col;
            return acc;
          }, {}),
        });
        setMapReady(true);
      }
    }, 10);
  }, [groupRef.current, heatmapInstancerRef.current]);

  const samples = useMemo(() => {
    return pathData.samples.map((p) => {
      const canvasPoint = canonicalToCanvas(p, vp);
      return {
        x: canvasPoint.cx - coordMin.cx,
        y: canvasPoint.cy - coordMin.cy,
        value: 1,
      };
    });
  }, [pathData.samples, vp, offsetY, offsetX]);

  useMemo(() => {
    if (coordsReady !== undefined && pathData.samples.length && mapReady) {
      if (heatmapInstancerRef.current && groupRef?.current) {
        heatmapInstancerRef.current.setData({
          max: 2,
          min: 0,
          data: samples,
        });
        heatmapInstancerRef.current.repaint()

      }
    }
  }, [rows, pathData.samples, coordsReady, mapReady, vp, offsetX, offsetY, heatmapInstancerRef.current, groupRef?.current]);

  return (
    <Html
      divProps={{
        style: {
          width: '100%',
          height: '100%',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          right: 0,
        },
        id: 'heatmap-canvas-layer',
      }}
/*      transformFunc={( transformAttrs ) => ({
        ...transformAttrs,
        x: transformAttrs.x + offsetX * transformAttrs.scaleX,
        y: transformAttrs.y + offsetY * transformAttrs.scaleY,
      })}*/
    >
      <div
        ref={groupRef}
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          right: 0,
        }}
      />
    </Html>
  );
};

export default HeatMap;
